import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import type {
  BusinessRelationshipDetail,
  UntradedDrug,
} from '@/entities/business-relationship/api/businessRelationship.api';
import { useUntradedDrugs } from '@/entities/business-relationship/hooks/useBusinessRelationships';
import { ErrorState } from '@/shared/components/StateViews';
import { Icon } from '@/shared/components/ui/icon';
import {
  Sheet,
  SheetContent,
  SheetDescription,
} from '@/shared/components/ui/sheet';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { useLoadMoreSentinel } from '@/shared/hooks/useLoadMoreSentinel';
import { cn } from '@/shared/lib/utils';
import { formatNumber, formatShortYm } from '@/shared/utils/format';

import { AppOnlyActionButton } from './AppOnlyActionButton';
import { DrawerHeader, DrawerListCard, TabCount } from './drawerParts';

type MissedTab = 'possible' | 'impossible';

/**
 * Figma 거래처_놓치고있는의약품 (845:8248) — 행 하나.
 * 기본 845:8232: 의약품명 16 Bold · 제약사 15 · 처방 수량(파랑) + [필터링 신청](앱 전용)
 * 불가 845:8249: 이름 · 제약사를 비활성 색으로, 오른쪽에 빨간 사유 (warning-circle 14 + 13 SemiBold)
 */
const MissedDrugRow = ({ drug }: { drug: UntradedDrug }) => (
  <li className="flex flex-col gap-2">
    <p
      className={cn(
        'text-subtitle-16-b break-keep',
        drug.canApply ? 'text-fg-primary' : 'text-fg-disabled',
      )}
    >
      {drug.drugName}
    </p>
    <p
      className={cn(
        'text-body-15-r',
        drug.canApply ? 'text-fg-tertiary' : 'text-fg-disabled',
      )}
    >
      {drug.pharmaCompanyName ?? '제약사 정보 없음'}
    </p>
    <div className="flex items-center gap-2">
      <p className="flex flex-1 gap-2 text-body-15-sb whitespace-nowrap text-fg-accent">
        <span>처방 수량</span>
        <span>{formatNumber(drug.totalOutHospitalQuantity)}</span>
      </p>
      {drug.canApply ? (
        <AppOnlyActionButton
          feature="filteringApply"
          variant="grey"
          size="small"
        >
          필터링 신청
        </AppOnlyActionButton>
      ) : (
        drug.blockReasonLabel && (
          <span className="flex items-center gap-1 px-1 text-body-13-sb text-fg-error">
            <Icon name="warning-circle-solid-14" className="text-red-500" />
            {drug.blockReasonLabel}
          </span>
        )
      )}
    </div>
  </li>
);

/** 한 탭(가능 · 불가) 목록 — 끝이 보이면 다음 페이지를 받는다 */
const DrugList = ({
  query,
  emptyMessage,
}: {
  query: ReturnType<typeof useUntradedDrugs>;
  emptyMessage: string;
}) => {
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const sentinelRef = useLoadMoreSentinel({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  if (query.isPending)
    return (
      <div className="flex flex-col gap-4 rounded-20 bg-surface-white p-6">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-24 w-full rounded-16" />
        ))}
      </div>
    );
  if (query.isError)
    return (
      <div className="rounded-20 bg-surface-white">
        <ErrorState onRetry={() => void query.refetch()} />
      </div>
    );

  const items = data?.pages.flatMap((p) => p.content) ?? [];
  if (items.length === 0)
    return (
      <p className="rounded-20 bg-surface-white px-6 py-10 text-center text-body-14-m text-fg-tertiary">
        {emptyMessage}
      </p>
    );

  return (
    <>
      <DrawerListCard>
        {items.map((d, i) => (
          <MissedDrugRow
            key={`${d.drugCode}-${d.pharmaCompanyId}-${i}`}
            drug={d}
          />
        ))}
      </DrawerListCard>
      {hasNextPage && (
        <div ref={sentinelRef} className="flex justify-center p-5" aria-hidden>
          {isFetchingNextPage && (
            <Loader2 className="size-5 animate-spin text-fg-light" />
          )}
        </div>
      )}
    </>
  );
};

/** 탭 본문 — 신청 가능(canApply=true) · 불가(false)를 따로 불러 각자 페이징한다. 탭 숫자는 서버 전체 건수 */
const MissedDrugsBody = ({
  detail,
  onClose,
}: {
  detail: BusinessRelationshipDetail;
  onClose: () => void;
}) => {
  const [tab, setTab] = useState<MissedTab>('possible');
  const possible = useUntradedDrugs(detail.relationshipId, true);
  // 불가 목록은 그 탭을 처음 볼 때 부른다
  const [impossibleSeen, setImpossibleSeen] = useState(false);
  const impossible = useUntradedDrugs(
    detail.relationshipId,
    false,
    impossibleSeen,
  );
  const first = possible.data?.pages[0];
  const ym = first?.standardYearMonth ?? detail.standardYearMonth;

  return (
    <Tabs
      value={tab}
      onValueChange={(v) => {
        setTab(v as MissedTab);
        if (v === 'impossible') setImpossibleSeen(true);
      }}
      className="flex min-h-0 flex-1 flex-col"
    >
      <DrawerHeader
        title="놓치고 있는 의약품"
        subtitle={ym ? `${formatShortYm(ym)} 확정 처방 기준` : null}
        clinicName={detail.clinicName}
        clinicTypeLabel={detail.clinicTypeLabel}
        onClose={onClose}
      >
        <TabsList className="w-full pt-2">
          <TabsTrigger value="possible" className="flex-1">
            <TabCount
              label="필터링 가능"
              count={first?.applicableCount ?? detail.untradedApplicableCount}
            />
          </TabsTrigger>
          <TabsTrigger value="impossible" className="flex-1">
            <TabCount
              label="필터링 불가"
              count={first?.nonApplicableCount ?? 0}
            />
          </TabsTrigger>
        </TabsList>
      </DrawerHeader>
      <SheetDescription className="sr-only">
        확정된 처방 중 아직 거래하지 않는 의약품입니다. 필터링 신청은 앱에서
        진행합니다.
      </SheetDescription>
      <div className="min-h-0 flex-1 overflow-y-auto p-6">
        {!ym ? (
          <p className="rounded-20 bg-surface-white px-6 py-10 text-center text-body-14-m text-fg-tertiary">
            확정된 처방 내역이 없어요.
          </p>
        ) : tab === 'possible' ? (
          <DrugList
            query={possible}
            emptyMessage="추가로 필터링할 수 있는 의약품이 없어요."
          />
        ) : (
          <DrugList
            query={impossible}
            emptyMessage="필터링할 수 없는 의약품이 없어요."
          />
        )}
      </div>
    </Tabs>
  );
};

/**
 * M-22 놓치고 있는 의약품 드로어 (640, Figma 842:7412 · 845:8388) — 목록은 웹, 신청은 앱(X6).
 * 기준은 최신 확정 처방월(standardYearMonth). 확정 처방이 없으면 null로 온다.
 * v1.0: 가능 · 불가를 한 목록의 구역이 아니라 탭으로 나눈다.
 */
export const MissedDrugsDrawer = ({
  detail,
  open,
  onClose,
}: {
  detail: BusinessRelationshipDetail;
  open: boolean;
  onClose: () => void;
}) => (
  <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
    <SheetContent side="right" className="bg-surface-grey">
      {open && <MissedDrugsBody detail={detail} onClose={onClose} />}
    </SheetContent>
  </Sheet>
);
